import { MenuItem, Select } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Section, SectionTitle, SectionSubtitle, Row, RowText, RowLabel } from '../../Profile/profile-styles'
import { MovieceptionSwitch } from '../../../components/Buttons/movieception-buttons'
import { SettingsResponse } from '../../../client/settings-api'
import { ThemePreference } from '../../../context/ThemeContext'

// ISO 3166-1 codes TMDB accepts for release windows.
const REGIONS = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'TR', label: 'Türkiye' },
  { code: 'JP', label: 'Japan' },
  { code: 'KR', label: 'South Korea' },
  { code: 'IN', label: 'India' },
  { code: 'BR', label: 'Brazil' },
  { code: 'MX', label: 'Mexico' },
]

interface Props {
  settings: SettingsResponse | null
  themePreference: ThemePreference
  isDisabled: boolean
  onThemeChange: (theme: ThemePreference) => void
  onUpdate: (payload: Partial<SettingsResponse>) => void
  setSettings: React.Dispatch<React.SetStateAction<SettingsResponse | null>>
}

const SettingsSelect = styled(Select<string>)({
  minWidth: 170,
})

export const PreferencesSection = ({
  settings,
  themePreference,
  isDisabled,
  onThemeChange,
  onUpdate,
  setSettings,
}: Props) => {
  // Feature toggles covered by "Enable all" (Dark Mode is appearance, not a feature).
  const allOn =
    (settings?.showProgressIndicators ?? true) &&
    (settings?.spoilerControl ?? true) &&
    (settings?.hideTmdbReviews ?? false)

  const handleToggleAll = (checked: boolean) => {
    if (!settings) return
    const payload = {
      showProgressIndicators: checked,
      spoilerControl: checked,
      hideTmdbReviews: checked,
    }
    setSettings({ ...settings, ...payload })
    onUpdate(payload)
  }

  return (
    <Section>
      <SectionTitle>Preferences</SectionTitle>
      <SectionSubtitle>
        Control theme and how content plays while you browse.
      </SectionSubtitle>

      <Row>
        <RowText>
          <RowLabel>Theme</RowLabel>
        </RowText>
        <SettingsSelect
          size='small'
          value={themePreference}
          onChange={(e) => onThemeChange(e.target.value as ThemePreference)}
          disabled={isDisabled}
        >
          <MenuItem value='light'>Light</MenuItem>
          <MenuItem value='dark'>Dark</MenuItem>
          <MenuItem value='system'>System (match device)</MenuItem>
        </SettingsSelect>
      </Row>

      <Row>
        <RowText>
          <RowLabel>Enable All Below</RowLabel>
        </RowText>
        <MovieceptionSwitch
          checked={allOn}
          onChange={(e) => handleToggleAll(e.target.checked)}
          disabled={isDisabled}
        />
      </Row>

      <Row>
        <RowText>
          <RowLabel>Show progress indicators</RowLabel>
        </RowText>
        <MovieceptionSwitch
          checked={settings?.showProgressIndicators ?? true}
          onChange={(e) => {
            if (!settings) return
            const checked = e.target.checked
            setSettings({ ...settings, showProgressIndicators: checked })
            onUpdate({ showProgressIndicators: checked })
          }}
          disabled={isDisabled}
        />
      </Row>

      <Row>
        <RowText>
          <RowLabel>Hide spoilers</RowLabel>
        </RowText>
        <MovieceptionSwitch
          checked={settings?.spoilerControl ?? true}
          onChange={(e) => {
            if (!settings) return
            const checked = e.target.checked
            setSettings({ ...settings, spoilerControl: checked })
            onUpdate({ spoilerControl: checked })
          }}
          disabled={isDisabled}
        />
      </Row>

      <Row>
        <RowText>
          <RowLabel>Hide TMDB reviews</RowLabel>
        </RowText>
        <MovieceptionSwitch
          checked={settings?.hideTmdbReviews ?? false}
          onChange={(e) => {
            if (!settings) return
            const checked = e.target.checked
            setSettings({ ...settings, hideTmdbReviews: checked })
            onUpdate({ hideTmdbReviews: checked })
          }}
          disabled={isDisabled}
        />
      </Row>

      <Row>
        <RowText>
          <RowLabel>Region</RowLabel>
        </RowText>
        <SettingsSelect
          size='small'
          value={settings?.region ?? 'US'}
          onChange={(e) => {
            if (!settings) return
            const region = e.target.value
            setSettings({ ...settings, region })
            onUpdate({ region })
          }}
          disabled={isDisabled}
        >
          {REGIONS.map((r) => (
            <MenuItem key={r.code} value={r.code}>
              {r.label}
            </MenuItem>
          ))}
        </SettingsSelect>
      </Row>

      <Row>
        <RowText>
          <RowLabel>Default Landing Page</RowLabel>
        </RowText>
        <SettingsSelect
          size='small'
          value={settings?.defaultLandingPage ?? 'home'}
          onChange={(e) => {
            if (!settings) return
            const defaultLandingPage = e.target.value
            setSettings({ ...settings, defaultLandingPage })
            onUpdate({ defaultLandingPage })
          }}
          disabled={isDisabled}
        >
          <MenuItem value='home'>Home</MenuItem>
          <MenuItem value='discover'>Discover</MenuItem>
          <MenuItem value='profile'>Profile</MenuItem>
        </SettingsSelect>
      </Row>
    </Section>
  )
}
